import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InvitationService, UserInvitationDetails } from 'src/app/services/app/invitation.service';

@Component({
  standalone: true,
  selector: 'app-accept-invitation',
  templateUrl: './accept-invitation.component.html',
  styleUrls: ['../subscriber-forgot-password/subscriber-forgot-password.component.css'],
  imports: [ReactiveFormsModule],
})
export class AcceptInvitationComponent implements OnInit {
  token = '';
  activeStep: 'details' | 'password' | 'success' = 'details';
  invitation: UserInvitationDetails | null = null;
  loadError = '';
  actionError = '';
  isSubmitting = false;
  hide = true;
  confirmhide = true;

  passwordForm = new FormGroup({
    Password: new FormControl('', [Validators.required]),
    ConfirmPassword: new FormControl('', [Validators.required]),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invitationService: InvitationService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!this.token) {
      this.loadError = 'Invalid invitation link.';
      return;
    }

    this.invitationService.getInvitation(this.token).subscribe({
      next: (details) => {
        this.invitation = details;
        if (details.invitationStatus === 'Completed') {
          this.activeStep = 'success';
        } else if (details.invitationStatus === 'Expired') {
          this.loadError = 'This invitation has expired.';
        }
      },
      error: () => {
        this.loadError = 'Invitation not found or no longer valid.';
      },
    });
  }

  get expiryLabel(): string {
    if (!this.invitation?.expiresOn) {
      return '';
    }
    const expires = new Date(this.invitation.expiresOn);
    const days = Math.max(0, Math.ceil((expires.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
    return `This invitation will expire in ${days} days.`;
  }

  acceptInvitation(): void {
    this.actionError = '';
    this.isSubmitting = true;
    this.invitationService.acceptInvitation(this.token).subscribe({
      next: (result) => {
        this.isSubmitting = false;
        if (result?.success === 1) {
          this.activeStep = 'password';
          return;
        }
        this.actionError = 'Invitation is invalid or has expired.';
      },
      error: () => {
        this.isSubmitting = false;
        this.actionError = 'Invitation is invalid or has expired.';
      },
    });
  }

  createPassword(): void {
    this.actionError = '';
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const password = String(this.passwordForm.value.Password ?? '');
    const confirmPassword = String(this.passwordForm.value.ConfirmPassword ?? '');
    if (password !== confirmPassword) {
      this.actionError = 'Password and confirm password do not match.';
      return;
    }

    this.isSubmitting = true;
    this.invitationService.completeInvitation(this.token, password, confirmPassword).subscribe({
      next: (result) => {
        this.isSubmitting = false;
        if (result?.success === 1) {
          this.activeStep = 'success';
          return;
        }
        this.actionError = 'Unable to create password. Please try again.';
      },
      error: (err) => {
        this.isSubmitting = false;
        this.actionError = err?.error?.message ?? 'Unable to create password. Please try again.';
      },
    });
  }

  goToLogin(): void {
    this.router.navigate(['/subscriber-login']);
  }
}
